import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, ModalPortal } from './ui';
import { VerificationBadge } from './VerificationBadge';
import { isAtLeastConfirmed, recordStatus, VERIFICATION_STATUS_LABEL } from '../utils/verification';
import type { ItemVerificationStatus } from '../types';

export function needsEditReset(status?: ItemVerificationStatus): boolean {
  return isAtLeastConfirmed(recordStatus({ verification_status: status }));
}

export const EDIT_RESET_ACK_STATE = { editResetAcknowledged: true } as const;

interface EditResetModalProps {
  status?: ItemVerificationStatus;
  continueLabel?: string;
  onCancel: () => void;
  onContinue: () => void;
}

export const EditResetModal: React.FC<EditResetModalProps> = ({
  status,
  continueLabel = 'Continue editing',
  onCancel,
  onContinue,
}) => {
  const resolved = recordStatus({ verification_status: status });
  const stageLabel = VERIFICATION_STATUS_LABEL[resolved];

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center print:hidden">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label="Close"
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-reset-title"
        className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-5 sm:p-6 m-0 sm:m-4"
      >
        <h2 id="edit-reset-title" className="text-lg font-bold text-gray-900">
          This item has already been {resolved === 'verified' ? 'verified' : 'accepted'}
        </h2>
        <div className="mt-2">
          <VerificationBadge status={resolved} size="sm" />
        </div>
        <p className="text-sm text-gray-600 mt-3 leading-relaxed">
          Changing a {stageLabel.toLowerCase()} record will reset it. It goes back to self-reported, or to
          evidence supplied if supporting files are still attached. A manager or verifier will need to
          review it again.
        </p>
        <div className="flex gap-3 mt-5">
          <Button variant="secondary" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button onClick={onContinue} className="flex-1">
            {continueLabel}
          </Button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

interface GuardedEditButtonProps {
  to: string;
  status?: ItemVerificationStatus;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'icon';
}

export const GuardedEditButton: React.FC<GuardedEditButtonProps> = ({
  to,
  status,
  children,
  className,
  size = 'sm',
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const button = (
    <Button type="button" variant="secondary" size={size} className={className}>
      {children}
    </Button>
  );

  if (!needsEditReset(status)) {
    return <Link to={to}>{button}</Link>;
  }

  return (
    <>
      <Button type="button" variant="secondary" size={size} className={className} onClick={() => setOpen(true)}>
        {children}
      </Button>
      {open && (
        <EditResetModal
          status={status}
          onCancel={() => setOpen(false)}
          onContinue={() => navigate(to, { state: EDIT_RESET_ACK_STATE })}
        />
      )}
    </>
  );
};
